import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getUiOptions } from "@rjsf/utils";

// Very light background colors for different nesting levels
const LEVEL_COLORS = [
  "#fafafa", // level 0
  "#f7fbff", // level 1
  "#f7fff9", // level 2
  "#fff9f7", // level 3
  "#fbf7ff", // level 4
  "#f9fff7", // level 5
];

function getDepthFromId(id) {
  if (!id) return 0;
  // rjsf ids look like: root, root_field, root_field_items_0_sub
  // count segments separated by "_" and subtract 1 so "root" => 0
  const segments = String(id).split("_");
  return Math.max(0, segments.length - 1);
}

// Object template: wraps nested objects in an Accordion for better readability
export function ObjectFieldTemplate(props) {
  const {
    title,
    description,
    properties,
    uiSchema,
    idSchema,
  } = props;

  const uiOptions = getUiOptions(uiSchema);
  const showAccordion = uiOptions.accordion !== false; // default true
  const defaultExpanded =
    typeof uiOptions.defaultExpanded === "boolean"
      ? uiOptions.defaultExpanded
      : true;
  const depth = getDepthFromId(idSchema?.$id);
  const bg = LEVEL_COLORS[depth % LEVEL_COLORS.length];

  // If there is no title and no description, just render children stacked
  if (!showAccordion || (!title && !description)) {
    return (
      <Stack spacing={2} sx={{ width: "100%", backgroundColor: bg, borderRadius: 1, p: 1 }}>
        {properties
          .filter((prop) => !prop.hidden)
          .map((prop) => (
            <Box key={prop.name}>{prop.content}</Box>
          ))}
      </Stack>
    );
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        width: "100%",
        backgroundColor: bg,
        mb: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        borderLeft: "4px solid",
        borderLeftColor: "divider",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">
          {title || idSchema?.$id || "Section"}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: bg }}>
        {description ? (
          <Box mb={1}>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        ) : null}
        <Stack spacing={2}>
          {properties
            .filter((prop) => !prop.hidden)
            .map((prop) => (
              <Box key={prop.name}>{prop.content}</Box>
            ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// Array template: card with top toolbar (title + add), items as accordions with actions
export function ArrayFieldTemplate(props) {
  const {
    title,
    description,
    canAdd,
    onAddClick,
    items,
    idSchema,
  } = props;

  const depth = getDepthFromId(idSchema?.$id);
  const bg = LEVEL_COLORS[depth % LEVEL_COLORS.length];

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2, borderRadius: 1, borderColor: "divider", width: "100%", backgroundColor: bg, borderLeft: "4px solid", borderLeftColor: "divider" }}
    >
      <Toolbar
        disableGutters
        sx={{ px: 2, py: 1.5, minHeight: "unset", borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {title || idSchema?.$id || "Items"}
        </Typography>
        {canAdd ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddClick}
            variant="contained"
          >
            Add
          </Button>
        ) : null}
      </Toolbar>
      {description ? (
        <Box px={2} pt={1}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      ) : null}
      <CardContent sx={{ pt: 1, backgroundColor: bg }}>
        <Stack spacing={1.5}>
          {items &&
            items.map((item) => (
              <Accordion
                key={item.key}
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  width: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  backgroundColor: bg,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: "100%" }}
                  >
                    <Chip
                      size="small"
                      label={`Item ${item.index + 1}`}
                      sx={{ fontWeight: 500 }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    {item.hasMoveUp ? (
                      <Button
                        size="small"
                        onClick={item.onReorderClick(item.index, item.index - 1)}
                        startIcon={<ArrowUpwardIcon />}
                      >
                        Up
                      </Button>
                    ) : null}
                    {item.hasMoveDown ? (
                      <Button
                        size="small"
                        onClick={item.onReorderClick(item.index, item.index + 1)}
                        startIcon={<ArrowDownwardIcon />}
                      >
                        Down
                      </Button>
                    ) : null}
                    {item.hasRemove ? (
                      <Button
                        color="error"
                        size="small"
                        style={{ marginRight: '10px' }}
                        onClick={item.onDropIndexClick(item.index)}
                        startIcon={<DeleteOutlineIcon />}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: bg }}>{item.children}</AccordionDetails>
              </Accordion>
            ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


